// ============================================================================
// CART OPTIONS V2 - DUAL PURCHASE INTERFACE
// ============================================================================
// Manages cart behavior for subscription products with two delivery options:
// - Button A: Trial kit ships WITH first subscription order
// - Button B: Trial kit ships SEPARATELY before subscription
// 
// Uses product metafield (subscription_config) for configuration with 
// sessionStorage fallback for performance and reliability
// ============================================================================

document.addEventListener('DOMContentLoaded', function() {
  
  // ============================================================================
  // DOM ELEMENTS
  // ============================================================================
  const buttons = document.querySelectorAll('.jd-cart-option-btn');
  const dynamicText = document.getElementById('jd-dynamic-text');
  const summaryStates = document.querySelectorAll('.jd-cart-summary__state');
  let currentOption = 'A';
  
  // ============================================================================
  // CONFIGURATION FROM METAFIELD
  // ============================================================================
  // Loaded from product.metafields.custom.subscription_config via Liquid
  // Stored in sessionStorage for persistence across page reloads
  const CONFIG = window.CART_VARIANT_CONFIG || {};
  const PLANS = CONFIG.plans || [];

  // Customer-facing messages from metafield
  // Used for both dynamic UI text and cart line item notes
  const CART_MESSAGES = {
    trialWithSub: CONFIG.cartMessages?.trialWithSub || '',
    trialFirst: CONFIG.cartMessages?.trialFirst || ''
  };

  // ============================================================================
  // VARIANT IDS
  // ============================================================================
  // VARIANT_A: Subscription variant where trial ships WITH first order
  // VARIANT_B: Subscription variant where trial ships BEFORE first order
  // OTP_VARIANT: One-time purchase trial pack (added/removed based on button selection)
  const VARIANT_A = String(CONFIG.variants?.defaultSubPack || '52623450079570');
  const VARIANT_B = String(CONFIG.variants?.subTrialPack || '52623450243410');
  const OTP_VARIANT = String(CONFIG.variants?.otpTrialPack || '52648902230354');
  
  // Validate critical configuration
  if (!CONFIG.variants?.defaultSubPack || !CONFIG.variants?.subTrialPack) {
    window.JD_DEBUG?.warn('Missing variant configuration - some features may not work correctly');
  }

  // ============================================================================
  // SELLING PLAN MAPPINGS
  // ============================================================================
  // Maps selling plan IDs between variants to maintain frequency selection
  // when swapping between Button A and Button B
  //
  // Example: If user has FREQUENT plan on Variant A and switches to Button B,
  // they should get the FREQUENT plan on Variant B (not a different frequency)
  const A_TO_B_PLANS = {}; // Maps Variant A plan IDs to Variant B plan IDs
  const B_TO_A_PLANS = {}; // Maps Variant B plan IDs to Variant A plan IDs

  // Build mappings dynamically from metafield data
  PLANS.forEach(plan => {
    if (plan.subPlanId && plan.trialPlanId) {
      A_TO_B_PLANS[String(plan.subPlanId)] = String(plan.trialPlanId);
      B_TO_A_PLANS[String(plan.trialPlanId)] = String(plan.subPlanId);
    }
  });

  if (window.JD_DEBUG) {
    window.JD_DEBUG.log('=== CART_VARIANT_CONFIG diagnostic ===');
    window.JD_DEBUG.log('Source:', window.CART_VARIANT_CONFIG ? 'window.CART_VARIANT_CONFIG' : 'FALLBACK (config not loaded)');
    window.JD_DEBUG.log('VARIANT_A:', VARIANT_A, '| raw:', CONFIG.variants?.defaultSubPack, '| type:', typeof CONFIG.variants?.defaultSubPack);
    window.JD_DEBUG.log('VARIANT_B:', VARIANT_B, '| raw:', CONFIG.variants?.subTrialPack, '| type:', typeof CONFIG.variants?.subTrialPack);
    window.JD_DEBUG.log('OTP_VARIANT:', OTP_VARIANT, '| raw:', CONFIG.variants?.otpTrialPack, '| type:', typeof CONFIG.variants?.otpTrialPack);
    window.JD_DEBUG.log('plans count:', PLANS.length);
    PLANS.forEach(function(p, i) {
      window.JD_DEBUG.log('  plan[' + i + '] id:', p.id, '| subPlanId:', p.subPlanId, '(', typeof p.subPlanId, ') | trialPlanId:', p.trialPlanId, '(', typeof p.trialPlanId, ')');
    });
    window.JD_DEBUG.log('A→B plan mappings:', A_TO_B_PLANS);
    window.JD_DEBUG.log('B→A plan mappings:', B_TO_A_PLANS);
    window.JD_DEBUG.log('cartMessages:', CART_MESSAGES);
    if (!window.CART_VARIANT_CONFIG) window.JD_DEBUG.warn('window.CART_VARIANT_CONFIG missing — check jd-cart-options.liquid loaded and metaobject is configured');
    if (!PLANS.length) window.JD_DEBUG.warn('plans array empty — A/B plan swapping will fail');
    if (!Object.keys(A_TO_B_PLANS).length) window.JD_DEBUG.warn('A→B plan map empty — check subPlanId and trialPlanId are populated in metaobject');
    fetch('/cart.js').then(function(r) { return r.json(); }).then(function(cart) {
      window.JD_DEBUG.log('=== CART ITEMS ===');
      (cart.items || []).forEach(function(item, i) {
        window.JD_DEBUG.log('  item[' + i + '] variant_id:', item.variant_id, '| qty:', item.quantity, '| selling_plan:', item.selling_plan_allocation?.selling_plan?.id ?? 'none', '| title:', item.title);
      });
      window.JD_DEBUG.log('==================');
    });
    window.JD_DEBUG.log('======================================');
  }

  // ============================================================================
  // CART API FUNCTIONS
  // ============================================================================
  
  /**
   * Fetches current cart state
   * @returns {Promise<Object>} Cart object with items array
   */
  function getCart() {
    return fetch('/cart.js').then(r => r.json());
  }
  
  /**
   * Adds item to cart with optional selling plan
   * @param {string} variantId - Shopify variant ID
   * @param {string|null} planId - Selling plan ID (null for one-time purchase)
   * @returns {Promise<Object>} Add to cart response
   */
  function addItem(variantId, planId = null) {
    const payload = { 
      items: [{ 
        id: String(variantId), 
        quantity: 1
      }] 
    };
    
    if (planId) {
      payload.items[0].selling_plan = String(planId);
    }
    
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(r => r.json());
  }
  
  /**
   * Removes item from cart by setting quantity to 0
   * @param {string} variantId - Shopify variant ID to remove
   * @returns {Promise<Object>} Cart update response
   */
  function removeItem(variantId) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: String(variantId), quantity: 0 })
    }).then(r => r.json());
  }

  /**
   * Updates line item properties (used for delivery notes)
   * @param {string} variantId - Shopify variant ID
   * @param {Object} properties - Key-value pairs for line item properties
   * @returns {Promise<Object>} Cart update response
   */
  function updateLineItemProperty(variantId, properties) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        id: String(variantId), 
        properties: properties 
      })
    }).then(r => r.json());
  }

  /**
   * Ensures all subscription items have quantity 1
   * Subscription products should never have quantity > 1 in cart
   * Called on page load and after cart operations
   * @returns {Promise<void>}
   */
  async function enforceSubscriptionQuantityLimits() {
    try {
      const cart = await getCart();
      let needsUpdate = false;
      
      // Check each item
      for (const item of cart.items) {
        // If it's a subscription item with quantity > 1
        if (item.selling_plan_allocation && item.quantity > 1) {
          window.JD_DEBUG?.log('Fixing subscription item quantity:', item.variant_id, 'from', item.quantity, 'to 1');
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              id: String(item.variant_id), 
              quantity: 1 
            })
          });
          needsUpdate = true;
        }
      }
      
      // Reload if any quantities were fixed
      if (needsUpdate) {
        location.reload();
      }
    } catch (error) {
      window.JD_DEBUG?.error('Error enforcing quantity limits:', error);
    }
  }
  
  // ============================================================================
  // MODAL
  // ============================================================================

  const modal = document.getElementById('jd-cart-modal');

  /**
   * Opens the modal in a given state.
   * Moves modal to document.body (portal), sets inert on all siblings,
   * locks body scroll, and focuses the first interactive element.
   * @param {'otp'|'loading'} state
   */
  function openModal(state) {
    if (!modal) return;

    // Portal: move to body so inert on siblings is straightforward
    if (modal.parentElement !== document.body) {
      // Capture color scheme context before leaving the scoped subtree
      var schemeEl = modal.closest('[data-color-scheme]');
      if (schemeEl) {
        modal.setAttribute('data-color-scheme', schemeEl.getAttribute('data-color-scheme'));
      }
      modal._origParent = modal.parentElement;
      modal._origNext = modal.nextSibling;
      document.body.appendChild(modal);
    }

    modal.querySelectorAll('.jd-cart-modal__state').forEach(function(el) {
      el.classList.toggle('is-active', el.dataset.state === state);
    });
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');

    // Lock scroll and block all interaction outside modal
    document.body.classList.add('jd-modal-open');
    Array.from(document.body.children).forEach(function(el) {
      if (el !== modal) el.inert = true;
    });

    // Focus first interactive element
    var focusable = modal.querySelector('a[href], button:not([disabled])');
    if (focusable) focusable.focus();
  }

  /**
   * Closes the modal.
   * Restores modal to its original DOM position, removes inert from siblings,
   * and unlocks body scroll.
   */
  function closeModal() {
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');

    // Unlock scroll and restore interaction
    document.body.classList.remove('jd-modal-open');
    Array.from(document.body.children).forEach(function(el) {
      el.inert = false;
    });

    // Restore modal to original DOM position
    if (modal._origParent) {
      modal._origParent.insertBefore(modal, modal._origNext || null);
      modal._origParent = null;
      modal._origNext = null;
    }
  }

  // ============================================================================
  // UI UPDATE FUNCTIONS
  // ============================================================================

  /**
   * Updates button UI state and dynamic text message
   * @param {string} option - 'A' or 'B'
   */
  function selectButton(option) {
    // Remove selected class from all buttons
    buttons.forEach(btn => {
      btn.classList.remove('selected');
    });
    
    // Add selected class to chosen button
    buttons.forEach(btn => {
      if (btn.dataset.option === option) {
        btn.classList.add('selected');
      }
    });
    
    currentOption = option;

    // Toggle summary state
    summaryStates.forEach(state => {
      state.hidden = state.dataset.summaryState !== option;
    });
  }

  function formatDate(date) {
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  }

  function formatMoney(pence) {
    var symbol = (window.SUBSCRIPTION_CONFIG && window.SUBSCRIPTION_CONFIG.currencySymbol) || '£';
    return symbol + (pence / 100).toFixed(2);
  }
  
  /**
   * Updates frequency display with current selling plan name
   * Looks for subscription item in cart and shows plan name (FREQUENT/REGULAR/LOW)
   */
  async function updateFrequency() {
    const frequencyEls = document.querySelectorAll('.jd-frequency-text');
    const firstBoxDateEls = document.querySelectorAll('.jd-first-box-date');
    const nextOrderDateEls = document.querySelectorAll('.jd-next-order-date');
    const nextOrderPriceEls = document.querySelectorAll('.jd-next-order-price');
    if (!frequencyEls.length) return;

    // First box date (state B) — today + configured ship delay (default 10), long month format
    const today = new Date();
    const shipDelay = CONFIG.firstOrderShipDelay || 10;
    const firstBoxDate = new Date(today);
    firstBoxDate.setDate(today.getDate() + shipDelay);
    const firstBoxDateStr = firstBoxDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    firstBoxDateEls.forEach(el => { el.innerHTML = firstBoxDateStr; });

    try {
      const cart = await getCart();

      const subItem = cart.items.find(item =>
        String(item.variant_id) === VARIANT_A ||
        String(item.variant_id) === VARIANT_B
      );

      // Fallback frequency from default plan (used if no selling plan found)
      const defaultPlan = PLANS.find(p => String(p.id) === String(CONFIG.defaultPlanId)) || PLANS[0];
      const fallbackWeeks = defaultPlan?.deliveryFrequency || 1;

      if (subItem && subItem.selling_plan_allocation) {
        // Get matching plan for frequency data — defined before use
        const sellingPlanId = String(subItem.selling_plan_allocation.selling_plan.id);
        const matchedPlan = PLANS.find(p =>
          String(p.subPlanId) === sellingPlanId || String(p.trialPlanId) === sellingPlanId
        );
        const weeks = matchedPlan?.deliveryFrequency || fallbackWeeks;
        const freqLabel = weeks + ' week' + (weeks > 1 ? 's' : '');
        frequencyEls.forEach(el => { el.innerHTML = freqLabel; });

        // Next order date (state A only — date in the when column)
        const nextDate = new Date(today);
        nextDate.setDate(today.getDate() + weeks * 7);
        nextOrderDateEls.forEach(el => { el.innerHTML = formatDate(nextDate); });

        // Next order price
        const subPrice = CONFIG.subscriptionBoxPrice;
        if (subPrice != null) {
          nextOrderPriceEls.forEach(el => { el.innerHTML = formatMoney(subPrice); });
        }
      } else {
        // No selling plan — use fallback frequency
        const freqLabel = fallbackWeeks + ' week' + (fallbackWeeks > 1 ? 's' : '');
        frequencyEls.forEach(el => { el.innerHTML = freqLabel; });
      }

    } catch (error) {
      window.JD_DEBUG?.error('Error updating frequency:', error);
      const freqLabel = (PLANS[0]?.deliveryFrequency || 1) + ' week' + ((PLANS[0]?.deliveryFrequency || 1) > 1 ? 's' : '');
      frequencyEls.forEach(el => { el.innerHTML = freqLabel; });
      firstBoxDateEls.forEach(el => { el.innerHTML = formatDate(firstBoxDate); });
    }
  }
  
  /**
   * Toggles loading state class and disables buttons.
   * Button clicks also open the loading modal via openModal('loading').
   * @param {boolean} show
   */
  function setLoadingState(show) {
    document.body.classList.toggle('cart-updating', show);
    buttons.forEach(btn => { btn.disabled = show; });
  }
  
  // ============================================================================
  // BUTTON CLICK HANDLERS
  // ============================================================================
  
  buttons.forEach(btn => {
    btn.addEventListener('click', async function() {
      const option = this.dataset.option;
      
      // Prevent clicking already active button
      if (this.classList.contains('selected')) {
        return; // Exit early - button already active
      }

      // Open loading modal immediately — covers buttons before any visual state changes
      openModal('loading');

      // Save button state to sessionStorage for page reload persistence
      sessionStorage.setItem('selectedButton', option);

      setLoadingState(true);
      
      try {
        const cart = await getCart();

        const currentAItem = cart.items.find(i => String(i.variant_id) === VARIANT_A);
        const currentBItem = cart.items.find(i => String(i.variant_id) === VARIANT_B);
        const hasOTP = cart.items.some(i => String(i.variant_id) === OTP_VARIANT);

        // ========================================================================
        // BUTTON A: Subscription (VARIANT_A) + OTP trial pack in cart
        // ========================================================================
        if (option === 'A') {

          if (currentBItem) {
            // Coming from Button B — swap VARIANT_B → VARIANT_A and add OTP
            const currentPlanId = currentBItem.selling_plan_allocation?.selling_plan?.id;
            const newPlanId = B_TO_A_PLANS[String(currentPlanId)];

            if (!newPlanId) {
              window.JD_DEBUG?.error('Plan mapping not found for:', currentPlanId);
              alert('Unable to switch delivery options. Please refresh and try again.');
              setLoadingState(false);
              return;
            }

            const newProperties = { ...currentBItem.properties || {} };
            if (CART_MESSAGES.trialWithSub) {
              newProperties['Free Trial'] = CART_MESSAGES.trialWithSub;
            } else {
              delete newProperties['Free Trial'];
            }

            await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [
                  { id: String(VARIANT_A), quantity: 1, selling_plan: String(newPlanId), properties: newProperties },
                  { id: String(OTP_VARIANT), quantity: 1 }
                ]
              })
            });

          } else if (currentAItem && !hasOTP) {
            // VARIANT_A in cart but OTP missing — add OTP only
            await addItem(OTP_VARIANT);

          } else {
            // Already correct state (VARIANT_A + OTP) — nothing to do
            setLoadingState(false);
            return;
          }

        // ========================================================================
        // BUTTON B: Trial subscription (VARIANT_B) only — no OTP
        // ========================================================================
        } else {

          if (currentAItem) {
            // Coming from Button A — swap VARIANT_A → VARIANT_B, remove OTP
            const currentPlanId = currentAItem.selling_plan_allocation?.selling_plan?.id;
            const newPlanId = A_TO_B_PLANS[String(currentPlanId)];

            if (!newPlanId) {
              window.JD_DEBUG?.error('Plan mapping not found for:', currentPlanId);
              alert('Unable to switch delivery options. Please refresh and try again.');
              setLoadingState(false);
              return;
            }

            const newProperties = { ...currentAItem.properties || {} };
            if (CART_MESSAGES.trialFirst) {
              newProperties['Free Trial'] = CART_MESSAGES.trialFirst;
            }

            await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                items: [{ id: String(VARIANT_B), quantity: 1, selling_plan: String(newPlanId), properties: newProperties }]
              })
            });

          } else {
            // Already correct state (VARIANT_B, no OTP) — nothing to do
            setLoadingState(false);
            return;
          }
        }

        // Reload page to show updated cart
        location.reload();
        
      } catch (error) {
        window.JD_DEBUG?.error('Error updating cart:', error);
        alert('Unable to update cart. Please refresh and try again.');
      } finally {
        setLoadingState(false);
      }
    });
  });
  
  // ============================================================================
  // PAGE LOAD INITIALIZATION
  // ============================================================================
  
  // Show OTP modal immediately if Liquid detected OTP in cart
  if (window.JD_OTP_IN_CART) {
    openModal('otp');
    return; // Skip subscription cart logic entirely
  }

  // Pre-select button A immediately (synchronous) so dynamic text is rendered
  // on first paint — prevents layout shift when async cart check completes.
  selectButton('A');

  (async function() {

    setLoadingState(true);

    // Determine button state from actual cart contents
    const initialCart = await getCart();
    const hasVariantA = initialCart.items.some(i => String(i.variant_id) === VARIANT_A);
    const hasVariantB = initialCart.items.some(i => String(i.variant_id) === VARIANT_B);

    // Correct selection based on actual cart state
    if (hasVariantB) {
      selectButton('B');
    } else {
      selectButton('A');
    }
    
    // Update frequency display
    await updateFrequency();
    
    // Enforce subscription quantity limits
    await enforceSubscriptionQuantityLimits();
    
    // Wait for cart to initialize
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // ========================================================================
    // AUTO-ADD OTP TRIAL PACK IF MISSING
    // ========================================================================
    // If Button A is active (Variant A in cart) but OTP trial is missing,
    // automatically add it. This handles edge cases where user manually
    // removes the trial or navigates directly to cart.
    
    let retries = 0;
    const maxRetries = 3;
    
    while (retries < maxRetries) {
      try {
        const cart = await getCart();
        
        // Check if cart loaded properly
        if (!cart || !cart.items) {
          throw new Error('Cart not ready');
        }
        
        const hasVariantA = cart.items.some(i => String(i.variant_id) === VARIANT_A);
        const hasOTP = cart.items.some(i => String(i.variant_id) === OTP_VARIANT);
        
        // If Variant A exists without OTP trial, add it
        if (hasVariantA && !hasOTP) {
          try {
            const result = await addItem(OTP_VARIANT);
            
            // Verify it was actually added
            const verifyCart = await getCart();
            const otpAdded = verifyCart.items.some(i => String(i.variant_id) === OTP_VARIANT);
            
            if (otpAdded) {
              location.reload(); // Reload to show updated cart
            } else {
              window.JD_DEBUG?.error('OTP trial failed to add - check variant ID or inventory');
            }
          } catch (error) {
            window.JD_DEBUG?.error('Error adding OTP trial:', error);
          }
        }
        
        break; // Success, exit loop
        
      } catch (error) {
        retries++;
        if (retries >= maxRetries) {
          window.JD_DEBUG?.error('Failed to initialize cart after retries:', error);
        } else {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }
    
    setLoadingState(false);
  })();
});